import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import PlaylistAddCheckCircleIcon from '@mui/icons-material/PlaylistAddCheckCircle';
import {
    AnimatedActionBar,
    AnimatedPage,
    AnimatedSection,
    metricMotion,
} from '../animations/MotionContainers';
import { EntradaActions, getMissingEntradaFields } from './EntradaActions';
import { EntradaCalculationDialog } from './EntradaCalculationDialog';
import { EntradaSection } from './EntradaSection';
import { EntradaStatCard } from './EntradaStatCard';
import { VariedadNavAutocomplete } from './VariedadNavAutocomplete';
import { useFlujosCatalogs } from '../../hooks/useFlujosCatalogs';
import { useFlujosVariedadesNav } from '../../hooks/useFlujosVariedadesNav';
import { buildEntradaPayload } from '../../utils/buildEntradaPayload';
import { downloadEntradaPreviewPdf } from '../../utils/entradaPreviewPdf';
import { calculateEntradaTotals, formatKilos, formatPercent } from '../../utils/flujoCalculations';
import { onlyDigits } from '../../utils/inputFormatters';
import { getEntradaChanges, toEditableEntrada } from '../../utils/entradaEditing';

const DEFAULT_PESO_PALOT = 194;
const DEFAULT_PESO_CAJA = 19;

const createInitialEntrada = (pesosPalot = [], pesosCaja = []) => {
    const defaultPesoPalot = pesosPalot.find((item) => Number(item.peso) === DEFAULT_PESO_PALOT);
    const defaultPesoCaja = pesosCaja.find((item) => Number(item.peso) === DEFAULT_PESO_CAJA);

    return ({
    codigo: null,
    fecha: new Date().toISOString().split('T')[0],
    idAlmacen: 0,
    idVariedad: '',
    porcentaje: '0',
    observaciones:
        '',
    kilos: '0',
    crucesPalots: '0',
    idPesoPalot: defaultPesoPalot?.id || 0,
    kilosPalot: defaultPesoPalot ? String(defaultPesoPalot.peso) : null,
    paletsEntrada: {
        1: '0',
        2: '0',
        3: '0',
    },
    idPesoCaja: defaultPesoCaja?.id || 0,
    pesoCaja: defaultPesoCaja ? String(defaultPesoCaja.peso) : null,
    otrosCalibres: '0',
    pOtrosCalibres: '0',
    retorno: '0',
    pRetorno: '0',
    verde: '0',
    pVerde: '0',
    segunda: '0',
    pSegunda: '0',
    bajoCalibre: '0',
    pBajoCalibre: '0',
    industria: '0',
    pIndustria: '0',
    podrido: '0',
    });
};

const numericInputProps = {
    inputMode: 'numeric',
    pattern: '[0-9]*',
};

const kgAdornment = {
    endAdornment: <InputAdornment position="end">kg</InputAdornment>,
};

const EntradaNumberField = ({ label, value, onChange, adornment, required }) => (
    <TextField
        fullWidth
        label={label}
        value={value}
        required={required}
        onChange={(event) => onChange(onlyDigits(event.target.value))}
        slotProps={{
            htmlInput: numericInputProps,
            input: adornment,
        }}
    />
);

const EntradaSelectField = ({ label, value, onChange, options, adornment }) => {
    const normalizedOptions = options.map((option) => (
        typeof option === 'object'
            ? { id: option.id, value: String(option.peso ?? option.value), label: String(option.peso ?? option.label ?? option.value) }
            : { id: option, value: String(option), label: String(option) }
    ));

    return (
        <TextField
            fullWidth
            select
            label={label}
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(event) => onChange(event.target.value)}
            slotProps={{ input: adornment }}
        >
            <MenuItem value="" disabled>
                Seleccione
            </MenuItem>
            {normalizedOptions.map((option) => (
                <MenuItem key={option.id} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};

const ClassificationRows = ({ rows, entrada, updateField }) => (
    <Grid container spacing={1.5} sx={{ alignItems: 'center' }}>
        <Grid size={{ xs: 4 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                Tipo
            </Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                Kilos (kg)
            </Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                Palots
            </Typography>
        </Grid>
        {rows.map(([label, kilosField, palotsField]) => (
            <React.Fragment key={label}>
                <Grid size={{ xs: 4 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{label}</Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                    <EntradaNumberField
                        label=""
                        value={entrada[kilosField]}
                        onChange={updateField(kilosField)}
                    />
                </Grid>
                <Grid size={{ xs: 4 }}>
                    {palotsField ? (
                        <EntradaNumberField
                            label=""
                            value={entrada[palotsField]}
                            onChange={updateField(palotsField)}
                        />
                    ) : null}
                </Grid>
            </React.Fragment>
        ))}
    </Grid>
);

export const EntradaForm = ({ initialEntrada = null, onBack, onCreate, onReload, onUpdate }) => {
    const editMode = Boolean(initialEntrada);
    const {
        almacenes,
        pesosCaja,
        pesosPalot,
        cajasPalet,
        pesoPalotMermaDefault,
        loading: catalogsLoading,
        error: catalogsError,
    } = useFlujosCatalogs();
    const {
        rows: variedades,
        loading: variedadesLoading,
        error: variedadesError,
    } = useFlujosVariedadesNav();
    const originalEntrada = useMemo(
        () => initialEntrada ? toEditableEntrada(initialEntrada, pesosPalot, pesosCaja) : null,
        [initialEntrada, pesosPalot, pesosCaja]
    );
    const [entrada, setEntrada] = useState(() => originalEntrada || createInitialEntrada());
    const [submitting, setSubmitting] = useState(false);
    const [submitFeedback, setSubmitFeedback] = useState({ message: '', severity: 'success' });
    const [reviewOpen, setReviewOpen] = useState(false);
    const [calculationOpen, setCalculationOpen] = useState(false);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [conflict, setConflict] = useState(false);
    const [changeReason, setChangeReason] = useState('');
    const totals = useMemo(
        () => calculateEntradaTotals(entrada, cajasPalet, { pesoPalotMerma: pesoPalotMermaDefault }),
        [cajasPalet, entrada, pesoPalotMermaDefault]
    );
    const entradaPayload = useMemo(() => buildEntradaPayload(entrada), [entrada]);
    const changes = useMemo(
        () => originalEntrada ? getEntradaChanges(originalEntrada, entrada, cajasPalet, almacenes, variedades) : [],
        [originalEntrada, entrada, cajasPalet, almacenes, variedades]
    );
    const missingFields = getMissingEntradaFields(entradaPayload);
    const missingVersion = editMode && (!Number.isInteger(initialEntrada.version) || initialEntrada.version < 1);
    const invalidWeightIds = editMode && !catalogsLoading && (
        !pesosPalot.some((item) => item.id === Number(entrada.idPesoPalot)) ||
        !pesosCaja.some((item) => item.id === Number(entrada.idPesoCaja))
    );
    const entradaWarnings = useMemo(() => {
        const warnings = [];

        if (totals.kilosClasificados > totals.kilosVolcados) {
            warnings.push('Los kilos clasificados no pueden ser mayores que los kilos volcados.');
        }

        if (totals.kgReales < 0) {
            warnings.push('Los kg reales no pueden ser negativos. Revisa precalibrado, retorno y verde.');
        }

        return warnings;
    }, [totals]);
    const addEntradaDisabled = entradaWarnings.length > 0;
    const entradaWarningMessage = entradaWarnings.join(' ');

    useEffect(() => {
        if (cajasPalet.length === 0) {
            return;
        }

        setEntrada((current) => {
            const nextPaletsEntrada = cajasPalet.reduce((acc, cajaPalet) => ({
                ...acc,
                [cajaPalet.id]: current.paletsEntrada?.[cajaPalet.id] || '0',
            }), {});

            return {
                ...current,
                paletsEntrada: nextPaletsEntrada,
            };
        });
    }, [cajasPalet]);

    useEffect(() => {
        if (editMode) return;
        const defaultPesoPalot = pesosPalot.find((item) => Number(item.peso) === DEFAULT_PESO_PALOT);
        const defaultPesoCaja = pesosCaja.find((item) => Number(item.peso) === DEFAULT_PESO_CAJA);

        if (!defaultPesoPalot && !defaultPesoCaja) {
            return;
        }

        setEntrada((current) => ({
            ...current,
            ...(!current.kilosPalot && defaultPesoPalot
                ? { idPesoPalot: defaultPesoPalot.id, kilosPalot: String(defaultPesoPalot.peso) }
                : {}),
            ...(!current.pesoCaja && defaultPesoCaja
                ? { idPesoCaja: defaultPesoCaja.id, pesoCaja: String(defaultPesoCaja.peso) }
                : {}),
        }));
    }, [editMode, pesosCaja, pesosPalot]);

    useEffect(() => {
        if (!editMode || catalogsLoading) return;

        setEntrada((current) => {
            const hydrated = toEditableEntrada(current, pesosPalot, pesosCaja);
            if (current.kilosPalot === hydrated.kilosPalot && current.pesoCaja === hydrated.pesoCaja) return current;
            return hydrated;
        });
    }, [editMode, catalogsLoading, pesosPalot, pesosCaja]);

    const updateField = (field) => (value) => {
        setEntrada((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const updatePesoPalot = (value) => {
        const selectedPeso = pesosPalot.find((pesoPalot) => String(pesoPalot.peso) === String(value));

        setEntrada((current) => ({
            ...current,
            idPesoPalot: selectedPeso?.id || 0,
            kilosPalot: value,
        }));
    };

    const updatePesoCaja = (value) => {
        const selectedPeso = pesosCaja.find((pesoCaja) => String(pesoCaja.peso) === String(value));

        setEntrada((current) => ({
            ...current,
            idPesoCaja: selectedPeso?.id || 0,
            pesoCaja: value,
        }));
    };

    const updatePaletsEntrada = (idCajaPalet) => (value) => {
        setEntrada((current) => ({
            ...current,
            paletsEntrada: {
                ...current.paletsEntrada,
                [idCajaPalet]: value,
            },
        }));
    };

    const resetEntrada = () => {
        setEntrada(editMode ? toEditableEntrada(initialEntrada, pesosPalot, pesosCaja) : createInitialEntrada(pesosPalot, pesosCaja));
        setChangeReason('');
    };

    const handleBack = () => {
        if (changes.length > 0) {
            setLeaveOpen(true);
        } else {
            onBack?.();
        }
    };

    const handleSubmitEntrada = async (payload) => {
        setSubmitting(true);

        try {
            await onCreate(payload);
            setSubmitFeedback({ message: 'Entrada guardada correctamente.', severity: 'success' });
            resetEntrada();
        } catch (error) {
            setSubmitFeedback({
                message: error?.response?.data?.message || error?.message || 'No se ha podido guardar la entrada.',
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handlePrintEntrada = () => downloadEntradaPreviewPdf({
        entrada,
        totals,
        almacenes,
        variedades,
        cajasPalet,
    });

    const handleUpdateEntrada = async () => {
        if (!onUpdate || !changeReason.trim() || changeReason.trim().length > 2000 ||
            missingFields.length || entradaWarnings.length || invalidWeightIds || missingVersion || conflict || !changes.length ||
            catalogsLoading || variedadesLoading) return;

        setSubmitting(true);
        try {
            await onUpdate({ payload: entradaPayload, motivo: changeReason.trim(), version: initialEntrada.version });
            setReviewOpen(false);
        } catch (error) {
            if (error?.response?.status === 409) {
                setConflict(true);
                setReviewOpen(false);
            } else {
                setSubmitFeedback({
                    message: error?.response?.data?.message || error?.message || 'No se han podido guardar los cambios.',
                    severity: 'error',
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatedPage>
                <Stack spacing={2}>
                    <AnimatedSection>
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: '0 0 auto',
                                }}
                            >
                                {editMode ? <FactCheckOutlinedIcon /> : <PlaylistAddCheckCircleIcon />}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h1">
                                    {editMode ? `Editar entrada #${initialEntrada.codigo}` : 'Control de flujos de fruta'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {editMode ? 'Revisa los cambios antes de guardarlos' : 'Registro y consulta de flujos de fruta'}
                                </Typography>
                            </Box>
                            {editMode && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ArrowBackRoundedIcon />}
                                    onClick={handleBack}
                                    sx={{ ml: 'auto', flexShrink: 0 }}
                                >
                                    Volver
                                </Button>
                            )}
                        </Stack>
                    </AnimatedSection>

                    {editMode && !onUpdate && (
                        <Alert severity="info" variant="outlined">
                            Puedes revisar los cambios. Guardar se habilitara cuando la migracion de auditoria este desplegada y probada.
                        </Alert>
                    )}
                    {editMode && onUpdate && missingVersion && (
                        <Alert severity="error" variant="outlined">
                            El servidor no ha enviado una version valida para esta entrada. No se puede guardar hasta que el GET la incluya.
                        </Alert>
                    )}
                    {editMode && invalidWeightIds && (
                        <Alert severity="error" variant="outlined">
                            No se puede identificar el peso del palot o de la caja guardado. Selecciona ambos pesos antes de guardar.
                        </Alert>
                    )}
                    {editMode && conflict && (
                        <Alert
                            severity="warning"
                            variant="outlined"
                            action={<Button color="inherit" size="small" onClick={onReload}>Recargar entrada</Button>}
                        >
                            Otra persona ha modificado esta entrada. Recargala para revisar la version actual; tus cambios locales se perderan.
                        </Alert>
                    )}

                    <AnimatedSection>
                        <EntradaSection icon={<FactCheckOutlinedIcon />} title="Datos generales">
                            {(catalogsLoading || variedadesLoading || catalogsError || variedadesError) && (
                                <Alert
                                    severity={catalogsError || variedadesError ? 'error' : 'info'}
                                    variant="outlined"
                                    sx={{ mb: 1.5 }}
                                >
                                    {catalogsError || variedadesError || 'Cargando catalogos...'}
                                </Alert>
                            )}
                            <Grid container spacing={1} sx={{ alignItems: 'start', justifyContent: 'space-between' }}>
                                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Fecha"
                                        value={entrada.fecha}
                                        onChange={(event) => updateField('fecha')(event.target.value)}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        select
                                        required
                                        label="Almacen"
                                        value={entrada.idAlmacen}
                                        onChange={(event) => updateField('idAlmacen')(Number(event.target.value))}
                                    >
                                        <MenuItem value={0} disabled>
                                            Seleccione almacen
                                        </MenuItem>
                                        {almacenes.map((almacen) => (
                                            <MenuItem key={almacen.id} value={almacen.id}>
                                                {almacen.nombre}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                                    <VariedadNavAutocomplete
                                        options={variedades}
                                        value={entrada.idVariedad}
                                        onChange={updateField('idVariedad')}
                                        loading={variedadesLoading}
                                        required
                                    />
                                </Grid>                              
                                <Grid size={{ xs: 12, sm: 6, md: 7 }}>
                                    <TextField
                                        label="Observaciones"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={entrada.observaciones}
                                        onChange={(event) => updateField('observaciones')(event.target.value)}
                                    />
                                </Grid>


                            </Grid>
                        </EntradaSection>
                    </AnimatedSection>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 4 }}>
                            <AnimatedSection>
                                <EntradaSection icon={<ViewInArOutlinedIcon />} title="Kilos volcados">
                                    <Grid container spacing={1.5}>
                                        <Grid size={{ xs: 12 }}>
                                            <EntradaNumberField
                                                label="Kilos"
                                                value={entrada.kilos}
                                                onChange={updateField('kilos')}
                                                adornment={kgAdornment}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, lg: 12 }}>
                                            <EntradaNumberField
                                                label="Grupos de 4 palots"
                                                value={entrada.crucesPalots}
                                                onChange={updateField('crucesPalots')}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, lg: 12 }}>
                                            <EntradaSelectField
                                                label="Peso del palot"
                                                value={entrada.kilosPalot}
                                                onChange={updatePesoPalot}
                                                options={pesosPalot}
                                                adornment={kgAdornment}
                                            />
                                        </Grid>
                                        {cajasPalet.map((cajaPalet) => (
                                            <Grid key={cajaPalet.id} size={{ xs: 12, sm: 4, lg: 12 }}>
                                                <EntradaNumberField
                                                    label={`Palets de ${cajaPalet.cajas} cajas`}
                                                    value={entrada.paletsEntrada[cajaPalet.id] || '0'}
                                                    onChange={updatePaletsEntrada(cajaPalet.id)}
                                                />
                                            </Grid>
                                        ))}
                                        <Grid size={{ xs: 12 }}>
                                            <EntradaSelectField
                                                label="Peso caja"
                                                value={entrada.pesoCaja}
                                                onChange={updatePesoCaja}
                                                options={pesosCaja}
                                                adornment={kgAdornment}
                                            />
                                        </Grid>
                                    </Grid>
                                </EntradaSection>
                            </AnimatedSection>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 8 }}>
                            <AnimatedSection>
                                <EntradaSection icon={<ScaleOutlinedIcon />} title="Clasificacion de fruta">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ClassificationRows
                                                rows={[
                                                    ['Precalibrado', 'otrosCalibres', 'pOtrosCalibres'],
                                                    ['Retorno', 'retorno', 'pRetorno'],
                                                    ['Verde', 'verde', 'pVerde'],
                                                    ['Segunda', 'segunda', 'pSegunda'],
                                                ]}
                                                entrada={entrada}
                                                updateField={updateField}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ClassificationRows
                                                rows={[
                                                    ['Bajo calibre', 'bajoCalibre', 'pBajoCalibre'],
                                                    ['Industria', 'industria', 'pIndustria'],
                                                    ['Podrido', 'podrido'],
                                                ]}
                                                entrada={entrada}
                                                updateField={updateField}
                                            />
                                        </Grid>
                                    </Grid>
                                </EntradaSection>
                            </AnimatedSection>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<ScaleOutlinedIcon />}
                                    title="Kilos volcados"
                                    value={formatKilos(totals.kilosVolcados)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<Inventory2OutlinedIcon />}
                                    title="Kilos clasificados"
                                    value={formatKilos(totals.kilosClasificados)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<TrendingUpOutlinedIcon />}
                                    title="Kilos sin clasificar"
                                    value={formatKilos(totals.kilosSinClasificar)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<PercentOutlinedIcon />}
                                    title="Rendimiento"
                                    value={formatPercent(totals.rendimiento)}
                                    suffix="%"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<AccountTreeOutlinedIcon />}
                                    title="Total palots"
                                    value={formatKilos(totals.totalPalots)}
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<ScaleOutlinedIcon />}
                                    title="Kg reales"
                                    value={formatKilos(totals.kgReales)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<PercentOutlinedIcon />}
                                    title="% primera"
                                    value={formatPercent(totals.percentages.primera)}
                                    suffix="%"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 1.5 }}>
                            <AnimatedSection variant={metricMotion}>
                                <EntradaStatCard
                                    icon={<TrendingUpOutlinedIcon />}
                                    title="Kilos primera"
                                    value={formatKilos(totals.kilosPrimera)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                    </Grid>

                    {/* <AnimatedSection>
                        <EntradaSection icon={<CommentOutlinedIcon />} title="Observaciones">
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={entrada.observaciones}
                                onChange={(event) => updateField('observaciones')(event.target.value)}
                            />
                        </EntradaSection>
                    </AnimatedSection> */}

                    <AnimatedActionBar>
                        {editMode ? (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <Button
                                    variant="contained"
                                    startIcon={<FactCheckOutlinedIcon />}
                                    onClick={() => setReviewOpen(true)}
                                    disabled={changes.length === 0 || catalogsLoading || variedadesLoading}
                                >
                                    Revisar cambios ({changes.length})
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ListAltOutlinedIcon />}
                                    onClick={() => setCalculationOpen(true)}
                                >
                                    Datos
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<RestoreOutlinedIcon />}
                                    onClick={resetEntrada}
                                    disabled={changes.length === 0}
                                >
                                    Restaurar original
                                </Button>
                            </Stack>
                        ) : (
                            <EntradaActions
                                payload={entradaPayload}
                                totals={totals}
                                onPrint={handlePrintEntrada}
                                onReset={resetEntrada}
                                onSubmit={handleSubmitEntrada}
                                addDisabled={addEntradaDisabled}
                                printDisabled={catalogsLoading || variedadesLoading}
                                submitting={submitting}
                            />
                        )}
                    </AnimatedActionBar>

                    {editMode && (
                        <>
                            <EntradaCalculationDialog
                                open={calculationOpen}
                                onClose={() => setCalculationOpen(false)}
                                totals={totals}
                            />
                            <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
                                <DialogTitle>Revisar cambios de la entrada #{initialEntrada.codigo}</DialogTitle>
                                <DialogContent>
                                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                                        {changes.map((change) => (
                                            <Box key={change.key} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                                                <Typography sx={{ fontWeight: 700 }}>{change.label}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {change.before || '-'} -&gt; {change.after || '-'}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {missingFields.length > 0 && (
                                            <Alert severity="error">Faltan campos obligatorios: {missingFields.join(', ')}</Alert>
                                        )}
                                        {entradaWarnings.length > 0 && (
                                            <Alert severity="error">{entradaWarningMessage}</Alert>
                                        )}
                                        {invalidWeightIds && (
                                            <Alert severity="error">Selecciona un peso de palot y un peso de caja validos.</Alert>
                                        )}
                                        {onUpdate && missingVersion && (
                                            <Alert severity="error">Falta la version de la entrada.</Alert>
                                        )}
                                        <TextField
                                            label="Motivo del cambio"
                                            multiline
                                            minRows={2}
                                            required
                                            value={changeReason}
                                            onChange={(event) => setChangeReason(event.target.value)}
                                            slotProps={{ htmlInput: { maxLength: 2000 } }}
                                            helperText={`${changeReason.length}/2000`}
                                        />
                                        {!onUpdate && (
                                            <Alert severity="info">La edicion se habilitara tras desplegar y probar la migracion de auditoria.</Alert>
                                        )}
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button color="secondary" onClick={() => setReviewOpen(false)}>Cerrar</Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveOutlinedIcon />}
                                        onClick={handleUpdateEntrada}
                                        disabled={!onUpdate || !changeReason.trim() || changeReason.trim().length > 2000 ||
                                            missingFields.length > 0 || entradaWarnings.length > 0 || invalidWeightIds ||
                                            missingVersion || conflict || submitting}
                                    >
                                        Guardar cambios
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)} maxWidth="xs" fullWidth>
                                <DialogTitle>Salir sin guardar</DialogTitle>
                                <DialogContent>
                                    <Typography>Los cambios de esta entrada se perderan.</Typography>
                                </DialogContent>
                                <DialogActions>
                                    <Button color="secondary" onClick={() => setLeaveOpen(false)}>Seguir editando</Button>
                                    <Button color="error" onClick={onBack}>Salir</Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    )}

                    <Snackbar
                        open={Boolean(entradaWarningMessage)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert severity="warning" variant="filled">
                            {entradaWarningMessage}
                        </Alert>
                    </Snackbar>
                    <Snackbar
                        open={Boolean(submitFeedback.message)}
                        autoHideDuration={4500}
                        onClose={() => setSubmitFeedback((current) => ({ ...current, message: '' }))}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert
                            severity={submitFeedback.severity}
                            variant="filled"
                            onClose={() => setSubmitFeedback((current) => ({ ...current, message: '' }))}
                        >
                            {submitFeedback.message}
                        </Alert>
                    </Snackbar>
                </Stack>
        </AnimatedPage>
    );
};
